import {
  ColumnLayout,
  Box,
  Button,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components-themed";
import { useLocation, useNavigate } from "react-router-dom";
import { COMMON_LABELS, stringToBoolean } from "../default-constants";
import { useAppDispatch } from "../../app/hook";
import {
  useLazyGetIndividualHandlingConfirmationDownloadQuery,
  usePutIndividualHandlingConfirmationTargetUpdateMutation,
} from "../../features/api";
import { useCallback, useState } from "react";
import {
  ConfirmationUpdateStoreInfo,
  ConfirmationUpdateMerchantInfo,
  IndividualHandlingConfirmationUpdateRequest,
  ConfirmationMerchantInfo,
  ConfirmationStoreInfo,
  IndividualHandlingSearchRequest,
  IndividualHandlingDownloadRequest,
} from "../../features/api-interfaces";
import { showModal } from "../UI/modal/modal-slice";
import { differenceWith, isEmpty, isEqual, isNull, isNil } from "lodash";
import { IconButton } from "../UI/button/IconButton";

interface IFooter {
  applicationNo: string;
  searchCriteria: IndividualHandlingSearchRequest;
  isMerchantRegistered: boolean;
  merchant: ConfirmationMerchantInfo | undefined;
  registeredStoreList: ConfirmationStoreInfo[];
  storeList: ConfirmationStoreInfo[] | undefined;
  disabledUpdateButton: boolean;
  disabledDownloadButton: boolean;
  refetch: Function;
}

/**
 * フッターのボタン群
 */
const Footer = (props: IFooter) => {
  const {
    applicationNo,
    searchCriteria: { individualHandlingReason, status },
    isMerchantRegistered,
    merchant,
    registeredStoreList,
    storeList,
    disabledUpdateButton,
    disabledDownloadButton,
    refetch,
  } = props;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const isBackToList = stringToBoolean(query.get("isBackToList"));
  const [isDownloading, setIsDownloading] = useState(false);
  const [putIndividualHandlingConfirmationTargetUpdate] =
    usePutIndividualHandlingConfirmationTargetUpdateMutation();
  const [lazyDownloadTrigger] =
    useLazyGetIndividualHandlingConfirmationDownloadQuery();

  /**
   * 戻るボタンを押したときの処理.
   */
  const backOnClick = () => {
    if (!isBackToList) {
      navigate(`/application-status/${applicationNo}`);
    } else {
      navigate(`/individual-handling-confirmation?isBackFromDetail=true`);
    }
  };

  /**
   * ダウンロードボタンを押下したときの処理.
   */
  const downloadOnClick = () => {
    setIsDownloading(true);
    const request: IndividualHandlingDownloadRequest = {
      applicationNo: Number(applicationNo ?? -1),
      search: {
        individualHandlingReason: isEmpty(individualHandlingReason)
          ? undefined
          : individualHandlingReason,
        status: isEmpty(status) || !isNil(merchant) ? undefined : status,
      },
    };
    lazyDownloadTrigger(request)
      .unwrap()
      .then()
      .finally(() => {
        setIsDownloading(false);
      });
  };

  /**
   * 更新ボタンを押下したときの処理.
   */
  const updateOnClick = useCallback(() => {
    dispatch(
      showModal({
        content: COMMON_LABELS.CONTENTS.UPDATE_CONFIRM,
        positiveFn: () => {
          // コメントまたは確認結果を更新した店舗の個別取扱を抽出する
          const updateStoreItems = differenceWith(
            storeList,
            registeredStoreList,
            isEqual
          )
            .filter(
              (store) =>
                !isNull(store.confirmationResult) || !isNull(store.comment)
            )
            .map(
              (store) =>
                ({
                  applicationStatusNo: store.applicationStatusNo,
                  individualHandlingReason: store.individualHandlingReason,
                  confirmationResult: store.confirmationResult,
                  comment: store.comment,
                  addressList: store.addressList,
                  addressType: store.addressType,
                } as ConfirmationUpdateStoreInfo)
            );

          const updateMerchant = isMerchantRegistered
            ? undefined
            : ({
              applicationStatusNo: merchant?.applicationStatusNo,
              comment: merchant?.comment,
              rateCategoryCode: merchant?.plannedRateCategoryCode,
              merchantInfoList: merchant?.merchantInfoList
            } as ConfirmationUpdateMerchantInfo);
          const request: IndividualHandlingConfirmationUpdateRequest = {
            merchant: updateMerchant,
            storeInfoList: updateStoreItems ?? undefined,
          };
          putIndividualHandlingConfirmationTargetUpdate(request).then(
            (result: any) => {
              if (
                !("error" in result) &&
                !result.data.status.includes("error")
              ) {
                if (!isBackToList) {
                  navigate(`/application-status/${applicationNo}`);
                } else {
                  refetch();
                }
              }
            }
          );
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeList,
    registeredStoreList,
    isMerchantRegistered,
    merchant,
    isBackToList,
    applicationNo,
  ]);

  return (
    <ColumnLayout columns={2}>
      <Box float={"left"}>
        <Button formAction={"none"} variant={"link"} onClick={backOnClick}>
          {COMMON_LABELS.BTNS.BACK}
        </Button>
      </Box>

      <Box float={"right"}>
        <SpaceBetween direction={"horizontal"} size={"l"}>
          <SpaceBetween direction={"horizontal"} size={"xs"}>
            {isDownloading && (
              <>
                <Spinner size="big" />
                {COMMON_LABELS.CONTENTS.DL_RUN}
              </>
            )}
            <IconButton
              iconName={"download"}
              label={COMMON_LABELS.BTNS.DL}
              onClick={downloadOnClick}
              disabled={disabledDownloadButton || isDownloading}
            />
          </SpaceBetween>
          <Button
            variant={"primary"}
            onClick={updateOnClick}
            disabled={disabledUpdateButton}
          >
            {COMMON_LABELS.BTNS.UPDATE}
          </Button>
        </SpaceBetween>
      </Box>
    </ColumnLayout>
  );
};

export default Footer;
